import React from 'react';
import { motion } from 'framer-motion';
export interface PercentageBarProps {
  /** The percentage to display (0-100) */
  percentage: number;
  /** Optional label above the bar */
  label?: string;
  /** Optional second percentage for comparison */
  comparePercentage?: number;
  /** Labels for the percentages */
  percentageLabel?: string;
  compareLabel?: string;
  /** Show percentage text on the bar */
  showValue?: boolean;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
function formatPercent(value: number): string {
  return new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0
  }).format(value);
}
function SingleBar({
  percentage,
  color,
  barLabel,
  showValue = true,
  delay = 0
}: {
  percentage: number;
  color: string;
  barLabel?: string;
  showValue?: boolean;
  delay?: number;
}) {
  const clamped = clamp(percentage, 0, 100);
  return <div className="space-y-1.5" data-id="element-13286">
      {barLabel && <div className="text-xs font-medium text-slate-600" data-id="element-13287">{barLabel}</div>}
      <div className="relative h-8 bg-slate-100 rounded-lg overflow-hidden border border-slate-200" data-id="element-13288">
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${clamped}%`
      }} transition={{
        delay,
        duration: 0.6,
        ease: 'easeOut'
      }} className={`absolute inset-y-0 left-0 rounded-lg ${color}`} data-id="element-13289" />
        {/* Grid lines at 25%, 50%, 75% */}
        {[25, 50, 75].map(mark => <div key={mark} className="absolute inset-y-0 w-px bg-slate-300/50" style={{
        left: `${mark}%`
      }} data-id="element-13290" />)}
        {/* Value label */}
        {showValue && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: delay + 0.4
      }} className="absolute inset-0 flex items-center justify-center text-sm font-bold" data-id="element-13291">
            <span className={clamped > 45 ? 'text-white' : 'text-slate-700'} data-id="element-13292">
              {formatPercent(clamped)}%
            </span>
          </motion.div>}
      </div>
      {/* Scale markers */}
      <div className="flex justify-between text-[10px] text-slate-400 px-0.5" data-id="element-13293">
        <span data-id="element-13294">0%</span>
        <span data-id="element-13295">25%</span>
        <span data-id="element-13296">50%</span>
        <span data-id="element-13297">75%</span>
        <span data-id="element-13298">100%</span>
      </div>
    </div>;
}
export function PercentageBar({
  percentage,
  label,
  comparePercentage,
  percentageLabel,
  compareLabel,
  showValue = true,
  mode = 'support'
}: PercentageBarProps) {
  if (!isFiniteNumber(percentage)) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-13299">
        <p className="text-red-600 text-sm font-medium" data-id="element-13300">
          Ongeldige percentage waarde
        </p>
      </div>;
  }
  const hasCompare = isFiniteNumber(comparePercentage);
  const clamped = clamp(percentage, 0, 100);
  // Stimulus mode: compact
  if (mode === 'stimulus') {
    return <div className="bg-blue-50 rounded-xl px-5 py-4 border border-blue-200" data-id="element-13301">
        {label && <p className="text-sm font-semibold text-blue-800 mb-3 text-center" data-id="element-13302">
            {label}
          </p>}
        <SingleBar percentage={clamped} color="bg-blue-500" barLabel={percentageLabel} showValue={showValue} data-id="element-13303" />
        {hasCompare && <div className="mt-3" data-id="element-13304">
            <SingleBar percentage={clamp(comparePercentage!, 0, 100)} color="bg-amber-500" barLabel={compareLabel} showValue={showValue} delay={0.2} data-id="element-13305" />
          </div>}
      </div>;
  }
  // Support mode: educational panel
  const fractionText = (() => {
    if (clamped === 25) return '¼';
    if (clamped === 50) return '½';
    if (clamped === 75) return '¾';
    if (clamped === 10) return '¹⁄₁₀';
    if (clamped === 20) return '¹⁄₅';
    if (clamped === 33 || clamped === 33.3) return '⅓';
    if (clamped === 40) return '⅖';
    if (clamped === 60) return '⅗';
    if (clamped === 80) return '⅘';
    if (clamped === 66 || clamped === 66.6 || clamped === 66.7) return '⅔';
    return null;
  })();
  const decimalText = formatPercent(clamped / 100);
  return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-200 shadow-sm" data-id="element-13306">
      {label && <div className="mb-5 text-center" data-id="element-13307">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-blue-200 shadow-sm" data-id="element-13308">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" data-id="element-13309" />
            <span className="text-sm font-bold text-blue-900" data-id="element-13310">{label}</span>
          </div>
        </div>}

      <div className="space-y-4" data-id="element-13311">
        <SingleBar percentage={clamped} color="bg-blue-500" barLabel={percentageLabel} showValue={showValue} data-id="element-13312" />

        {hasCompare && <SingleBar percentage={clamp(comparePercentage!, 0, 100)} color="bg-amber-500" barLabel={compareLabel} showValue={showValue} delay={0.2} data-id="element-13313" />}

        {/* Educational explanation */}
        <div className="bg-white/80 rounded-xl p-4 border border-blue-100 shadow-sm" data-id="element-13314">
          <div className="flex items-start gap-3" data-id="element-13315">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-13316">
              <span className="text-indigo-600 text-sm" data-id="element-13317">📊</span>
            </div>
            <div data-id="element-13318">
              <h4 className="text-sm font-bold text-indigo-900 mb-1" data-id="element-13319">
                Wat betekent dit?
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13320">
                <strong data-id="element-13321">{formatPercent(clamped)}%</strong> betekent{' '}
                <strong data-id="element-13322">{formatPercent(clamped)}</strong> van de{' '}
                <strong data-id="element-13323">100</strong>.
                {fractionText && <span data-id="element-13324">
                    {' '}
                    Als breuk is dat <strong data-id="element-13325">{fractionText}</strong>.
                  </span>}
                <span data-id="element-13326">
                  {' '}
                  Als kommagetal is dat <strong data-id="element-13327">{decimalText}</strong>.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}